import { PublicLayout } from '@/components/PublicLayout'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, ShieldCheck, Cpu, FileText } from 'lucide-react'
import { Link } from 'react-router-dom'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE_STANDARD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94]

// ---------------------------------------------------------------------------
// Team data
// ---------------------------------------------------------------------------

const team = [
  {
    name: 'Samiir',
    role: 'Founder & Lead Engineer',
    image: '/us/Adam.webp',
    bio: "Ten years as a freelance developer taught me that the biggest hurdle in this industry isn't code. It's trust. I founded Masonyt to take the risk out of the equation and empower experienced, hardworking engineers.",
  },
  {
    name: 'Sami',
    role: 'Co-Founder & Strategy',
    image: '/us/Samy.webp',
    bio: 'Talent is equally distributed around the world, but opportunity is not. Our goal at Masonyt is to dismantle those barriers and build a meritocratic stage where skills and drive are what matter.',
  },
]

// ---------------------------------------------------------------------------
// Explore cards data
// ---------------------------------------------------------------------------

const exploreCards = [
  {
    to: '/process',
    icon: FileText,
    title: 'Our Process',
    desc: 'An 8-phase lifecycle from definition to evolution, structured so nothing falls through the cracks.',
    hoverBorder: 'hover:border-accent',
  },
  {
    to: '/security',
    icon: ShieldCheck,
    title: 'Security First',
    desc: 'The Bedrock Standard and Keystone Protocol protect your data with enterprise-grade rigor.',
    hoverBorder: 'hover:border-primary',
  },
  {
    to: '/agentic',
    icon: Cpu,
    title: 'AI & Agents',
    desc: 'How we leverage Agentic Frameworks to accelerate development without losing control.',
    hoverBorder: 'hover:border-accent',
  },
]

// ---------------------------------------------------------------------------
// Reusable animation helpers
// ---------------------------------------------------------------------------

function useFadeUp(prefersReducedMotion: boolean | null) {
  return (delay: number, yStart = 40) => ({
    initial: prefersReducedMotion ? (false as const) : { opacity: 0, y: yStart },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 } as const,
    transition: { duration: 0.65, ease: EASE_STANDARD, delay },
  })
}

// ---------------------------------------------------------------------------
// Hero Section
// ---------------------------------------------------------------------------

function HeroSection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  return (
    <section className="relative px-8 pb-16 pt-24 md:pb-24 md:pt-32">
      {/* Gradient mesh */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 55% at 50% 30%, rgba(91,146,121,0.06) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 70% 60%, rgba(232,152,90,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1120px] text-center">
        <motion.h1
          {...fadeUp(0)}
          className="mx-auto mb-6 max-w-[720px] text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 'clamp(38px, 5vw, 56px)',
            fontWeight: 700,
            lineHeight: 1.08,
            letterSpacing: '-1.5px',
          }}
        >
          Built by builders,
          <br />
          for{' '}
          <span className="bg-gradient-to-br from-primary to-[#6BAE8A] bg-clip-text text-transparent">
            builders
          </span>
          .
        </motion.h1>

        <motion.p
          {...fadeUp(0.12, 30)}
          className="mx-auto max-w-[560px] text-lg leading-[1.65] text-muted-foreground"
        >
          We started Masonyt because we lived the problem ourselves: talented developers struggling
          to find clients who trusted them, and business owners terrified of hiring the wrong one.
          There had to be a better way.
        </motion.p>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// The Why Section
// ---------------------------------------------------------------------------

function TheWhySection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  return (
    <section className="bg-card px-8 py-[100px]">
      <div className="mx-auto max-w-[700px] text-center">
        <motion.blockquote
          {...fadeUp(0)}
          className="text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 'clamp(20px, 2.5vw, 26px)',
            fontWeight: 400,
            fontStyle: 'italic',
            lineHeight: 1.55,
            letterSpacing: '-0.3px',
          }}
        >
          "We got tired of seeing great ideas die because business owners couldn't find developers
          they could trust, and great developers getting overlooked because they didn't have the
          right connections. Masonyt exists to fix that disconnect."
        </motion.blockquote>

        <motion.div {...fadeUp(0.12)} className="mt-8 flex items-center justify-center gap-4">
          <div className="h-px w-12 bg-border" />
          <span className="text-xs font-bold uppercase tracking-[2.5px] text-accent">
            The Founders
          </span>
          <div className="h-px w-12 bg-border" />
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Team Section
// ---------------------------------------------------------------------------

function TeamSection() {
  const prefersReducedMotion = useReducedMotion()

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <section className="bg-background px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        {/* Section header */}
        <div className="mb-16 text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD }}
          >
            The Team
          </motion.div>

          <motion.h2
            className="mb-4 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
          >
            The People Behind Masonyt
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[480px] text-[17px] text-muted-foreground"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
          >
            Engineers who decided to build the platform they always wished existed.
          </motion.p>
        </div>

        {/* Team grid */}
        <div className="mx-auto grid max-w-[720px] grid-cols-1 gap-5 sm:grid-cols-2">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              className="group rounded-[20px] border border-border bg-card p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-[0_12px_32px_rgba(45,42,38,0.06)]"
              variants={cardVariants}
              custom={i}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              {/* Photo */}
              <div className="mb-6 flex justify-center">
                {member.image ? (
                  <div className="h-24 w-24 overflow-hidden rounded-full border-[3px] border-primary/20 shadow-[0_4px_12px_rgba(45,42,38,0.08)] transition-all duration-300 group-hover:border-primary/40 group-hover:shadow-[0_6px_20px_rgba(45,42,38,0.1)]">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div
                    className="flex h-24 w-24 items-center justify-center rounded-full border-[3px] border-primary/20 bg-primary/[0.08] text-2xl font-bold text-primary shadow-[0_4px_12px_rgba(45,42,38,0.08)]"
                    style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                  >
                    {member.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="text-center">
                <h3
                  className="mb-1 text-xl font-bold text-foreground"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  {member.name}
                </h3>
                <p className="mb-4 text-[13px] font-medium text-accent">{member.role}</p>
                <p className="text-sm leading-[1.65] text-muted-foreground">{member.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Explore Section
// ---------------------------------------------------------------------------

function ExploreSection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <section className="bg-card px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        {/* Section header */}
        <div className="mb-16 text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            {...fadeUp(0)}
          >
            Explore
          </motion.div>

          <motion.h2
            className="mb-4 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            {...fadeUp(0.1)}
          >
            Dive Deeper
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[460px] text-[17px] text-muted-foreground"
            {...fadeUp(0.2)}
          >
            Learn how the platform works under the hood.
          </motion.p>
        </div>

        {/* Illustration — the single visual break */}
        <motion.div
          className="mx-auto mb-14 max-w-[360px] rounded-[20px] border border-border bg-background p-6"
          initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD }}
        >
          <img
            src="/illustrations/about-us.svg"
            alt="About Masonyt"
            className="mx-auto h-auto w-full"
            loading="lazy"
          />
        </motion.div>

        {/* Cards */}
        <div className="grid gap-5 md:grid-cols-3">
          {exploreCards.map((card, i) => (
            <Link key={card.title} to={card.to} className="group">
              <motion.div
                className={`flex h-full flex-col rounded-[20px] border border-border bg-background p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(45,42,38,0.08)] ${card.hoverBorder}`}
                variants={cardVariants}
                custom={i}
                initial={prefersReducedMotion ? false : 'hidden'}
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
              >
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-[14px] bg-primary/[0.08] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110">
                  <card.icon className="h-7 w-7 text-primary" />
                </div>

                <h3
                  className="mb-3 text-2xl font-bold text-foreground"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  {card.title}
                </h3>

                <p className="mb-6 flex-grow text-sm leading-relaxed text-muted-foreground">
                  {card.desc}
                </p>

                <div className="flex items-center text-sm font-semibold text-primary transition-transform duration-300 group-hover:translate-x-2">
                  Learn more <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// CTA Section
// ---------------------------------------------------------------------------

function CTASection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  return (
    <section className="relative bg-background px-8 py-[140px] text-center">
      {/* Subtle gradient */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(232,152,90,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1120px]">
        <motion.div
          className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
          {...fadeUp(0)}
        >
          Get Started
        </motion.div>

        <motion.h2
          className="mb-6 text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 'clamp(36px, 4vw, 48px)',
            fontWeight: 700,
            letterSpacing: '-1px',
          }}
          {...fadeUp(0.1)}
        >
          Ready to Work Together?
        </motion.h2>

        <motion.p
          className="mx-auto mb-10 max-w-[460px] text-lg leading-relaxed text-muted-foreground"
          {...fadeUp(0.2)}
        >
          Whether you have a vision or the skills to build one, Masonyt is where it starts.
        </motion.p>

        <motion.div
          className="flex flex-col items-center justify-center gap-4 sm:flex-row"
          {...fadeUp(0.3)}
        >
          {/* Primary CTA — amber pill */}
          <motion.div
            className="inline-flex"
            whileHover={
              prefersReducedMotion
                ? {}
                : {
                    y: -2,
                    scale: 1.02,
                    boxShadow: '0 8px 32px rgba(232,152,90,0.35)',
                  }
            }
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2.5 rounded-full bg-accent px-10 py-4 text-lg font-bold text-accent-foreground shadow-[0_4px_24px_rgba(232,152,90,0.3)] transition-colors"
            >
              Join as Client
              <ArrowRight className="h-[18px] w-[18px]" />
            </Link>
          </motion.div>

          {/* Secondary CTA — outline primary */}
          <Link
            to="/join-developer"
            className="inline-flex items-center justify-center rounded-lg border border-primary px-8 py-4 text-lg font-bold text-primary transition-colors hover:bg-primary/[0.06]"
          >
            Join as Developer
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page Export
// ---------------------------------------------------------------------------

export default function InfoPage() {
  return (
    <PublicLayout>
      <div className="bg-background">
        <HeroSection />
        <TheWhySection />
        <TeamSection />
        <ExploreSection />
        <CTASection />
      </div>
    </PublicLayout>
  )
}
